import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpen, FileDown, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../lib/api";
import { fmtDateTime } from "../lib/format";

const API = process.env.REACT_APP_BACKEND_URL;

// Master-only: generate / read / download the AI-written PostWage User Manual (OpenAI, firm's own key).
export function UserManualPanel() {
  const [edition, setEdition] = useState("staff");
  const [info, setInfo] = useState(null);
  const [text, setText] = useState("");
  const [focus, setFocus] = useState("");
  const [running, setRunning] = useState(false);
  const endRef = useRef(null);

  const load = (ed = edition) => api.get(`/ai/manual?edition=${ed}`).then((r) => { setInfo(r.data); setText(r.data.markdown || ""); }).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(edition); }, [edition]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (running) endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [text, running]);

  const poll = async () => {
    try {
      const { data } = await api.get(`/ai/manual/progress?edition=${edition}`);
      setText(data.text || "");
      if (data.status === "running") return setTimeout(poll, 1500);
      setRunning(false);
      if (data.status === "error") toast.error(data.error || "Generation failed");
      if (data.status === "done") { toast.success("User manual generated"); load(); }
    } catch (e) { setRunning(false); toast.error(apiErr(e)); }
  };
  useEffect(() => { setRunning(false); api.get(`/ai/manual/progress?edition=${edition}`).then((r) => { if (r.data.status === "running") { setRunning(true); poll(); } }).catch(() => {}); }, [edition]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    setRunning(true); setText("");
    try {
      await api.post("/ai/manual/generate", { focus, edition });
      poll();
    } catch (e) { setRunning(false); toast.error(apiErr(e)); }
  };

  const dl = (ext) => window.open(`${API}/api/ai/manual.${ext}?edition=${edition}`, "_blank", "noopener");

  return (
    <div data-testid="user-manual-panel" className="space-y-5">
      <div className="border border-border bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="user-manual-editions">
              {(info?.editions || [{ id: "staff", title: "User Manual" }, { id: "client", title: "Client Portal Guide" }]).map((e) => (
                <button key={e.id} data-testid={`user-manual-edition-${e.id}`} onClick={() => setEdition(e.id)} disabled={running} title={e.audience}
                  className={`px-3 py-1.5 text-xs font-medium border transition-colors ${edition === e.id ? "bg-black text-white border-black" : "border-border hover:border-black"}`}>{e.title}</button>
              ))}
            </div>
            <h2 className="font-bold tracking-tight flex items-center gap-2"><BookOpen className="h-4 w-4" /> {info?.title || "User Manual"} (AI)
              {info?.stale && <span data-testid="user-manual-stale" className="text-[10px] font-mono uppercase tracking-widest border border-amber-500 text-amber-700 px-1.5 py-0.5">Outdated — refreshes automatically after deploy</span>}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              {edition === "client" ? "A plain-English guide for client Administrators and Payroll Preparers: quick start, every portal area, and why PostWage is different."
                : "Writes a complete PostWage manual from the live feature inventory and highlights what other payroll products don't offer."}
              Model: <span className="font-mono">{info?.model || "…"}</span>{info && !info.configured && <span className="text-red-600"> · OpenAI key missing</span>}
            </p>
            {info?.generated_at && <p data-testid="user-manual-meta" className="text-xs text-muted-foreground mt-1">Last generated {fmtDateTime(info.generated_at)} by {info.generated_by}</p>}
          </div>
          <div className="flex gap-2">
            <button data-testid="user-manual-pdf" onClick={() => dl("pdf")} disabled={!info?.generated_at || running} className="inline-flex items-center gap-1.5 border border-black bg-white px-4 py-2.5 text-sm font-medium hover:bg-black hover:text-white transition-colors disabled:opacity-40"><FileDown className="h-4 w-4" /> PDF</button>
            <button data-testid="user-manual-md" onClick={() => dl("md")} disabled={!info?.generated_at || running} className="inline-flex items-center gap-1.5 border border-border px-4 py-2.5 text-sm hover:border-black transition-colors disabled:opacity-40">Markdown</button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input data-testid="user-manual-focus" value={focus} onChange={(e) => setFocus(e.target.value)} disabled={running} placeholder="Optional instructions (e.g. 'write for client administrators', 'add a quick-start chapter')"
            className="flex-1 min-w-[280px] border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
          <button data-testid="user-manual-generate" onClick={generate} disabled={running || info?.configured === false}
            className="inline-flex items-center gap-1.5 bg-black text-white px-5 py-2.5 text-sm font-medium hover:bg-neutral-800 disabled:opacity-50">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {running ? "Writing…" : info?.generated_at ? "Regenerate" : "Generate manual"}
          </button>
        </div>
      </div>
      {(text || running) && (
        <article data-testid="user-manual-content" className="border border-border bg-white p-8 prose prose-neutral max-w-none prose-headings:tracking-tight prose-h1:text-3xl prose-h2:text-xl prose-h3:text-base prose-a:text-black">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
          {running && <span className="inline-block w-2 h-4 bg-black animate-pulse align-middle" />}
          <div ref={endRef} />
        </article>
      )}
    </div>
  );
}
